import React, { useEffect, useState } from 'react'
import { checkUserAvailable, getUserEvent } from 'slices/calendar/thunk';
import { format, addWeeks, startOfWeek, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import EventDetailModal from './EventDetailModal';
import CheckAvailabilityForm from './CheckAvailabilityForm';
import { useNavigate } from 'react-router-dom';
import Loading from 'Common/Components/Loading';
import './Index.scss';

const StaffSchedule = () => {
  document.title = `Schedule | EngageHub IQ`;
  const navigate = useNavigate();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any>([]);
  const [show, setShow] = useState<any>(false);
  const [eventDetail, setEventDetail] = useState<any>();
  const [searchContactText, setSearchContactText] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 20];

  const userEvents = useSelector((state: any) => state.Calendar.userEvents);
  const userFreeSlots = useSelector((state: any) => state.Calendar.userFreeSlots);
  const loading = useSelector((state: any) => state.Calendar.loading);

  // State to manage the current week's start date
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Generate days of the week based on currentWeek
  const daysOfWeek = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(currentWeek);
    date.setDate(currentWeek.getDate() + i);
    return format(date, 'EEE, MMM d');
  });

  const currentYear = format(currentWeek, 'yyyy');

  useEffect(() => {
    dispatch(getUserEvent())
  }, [dispatch])

  useEffect(() => {
    if (userEvents.length >= 0) {
      setData(userEvents)
    }

  }, [userEvents])

  useEffect(() => {
    if (userFreeSlots.length >= 0) {
      setData(userFreeSlots)
    }

  }, [userFreeSlots])


  const handleNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const handlePreviousWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, -1));
  };

  const handleEventClick = (event: any) => {
    if (!!event.id) {
      setEventDetail(event)
    }
    setShow(!show);
  };

  const getDetailCalendar = (event: any) => {
    if (!!event?.userId) {
      navigate(`/calendar?userId=${event?.userId}`);
    }
  }

  const handleCheckAvailability = (fromDate: string, toDate: string, time: string) => {
    dispatch(checkUserAvailable({ from: fromDate, to: toDate, timeZone: timeZone, timeRange: time }));
  };

  const handleDateChange = (dateString: string) => {
    const selectedDate = parseISO(dateString);
    const startOfCurrentWeek = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const endOfCurrentWeek = addWeeks(startOfCurrentWeek, 1);

    if (selectedDate < startOfCurrentWeek || selectedDate >= endOfCurrentWeek) {
      const newWeekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      setCurrentWeek(newWeekStart);
    }
  }

  const handleOnChangeOption = () => {
  }

  const filterSearchData = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchContactText(e.target.value);
  };
  const keysToSearch = ["name", "email", "phone"];
  const filteredEventData = Array.isArray(data)
    ? data.filter((item: any) =>
      keysToSearch.some((key: any) =>
        item[key]?.toLowerCase().includes(searchContactText.toLowerCase())
      )
    )
    : [];

  // Pagination calculations
  const totalItems = Array.isArray(filteredEventData) ? filteredEventData.length : 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = Array.isArray(filteredEventData) ? filteredEventData.slice(startIndex, endIndex) : [];

  const handlePageChange = (page: any) => {
    setCurrentPage(page);
  };

  return (
    <React.Fragment>
      <EventDetailModal show={show} eventData={eventDetail} onHide={() => setShow(!show)}></EventDetailModal>
      <div className="bg-white w-full h-[calc(100vh-70px)] overflow-hidden">
        <div className="!mb-0 p-2 sm:p-3 md:p-4 lg:p-5" id="staff-schedule">
          <div className="overflow-x-auto">
            <h6 className="text-15 mb-3">Schedule Management</h6>
            <CheckAvailabilityForm textValue={searchContactText} onClearSearch={() => { setSearchContactText("") }} onDateChange={handleDateChange} onCheckAvailability={handleCheckAvailability} onChangeOption={handleOnChangeOption} onSearch={filterSearchData} />
            {loading
              ? <><div className="bg-white min-h-[100px]"><Loading size="sm" color="blue" isVisible={true} /></div></>
              : (<>
                {filteredEventData.length > 0 ? (
                  <>
                    <div className="flex flex-row justify-between mb-4 items-center gap-0">
                      <div className="hidden sm:block ">
                        <div className="hidden sm:block text-slate-500">
                        </div>
                      </div>
                      <div className="text-base sm:text-lg font-bold order-2">{currentYear}</div>
                      <div className="flex justify-center items-center bg-custom-500 rounded order-3">
                        <button
                          onClick={handlePreviousWeek}
                          className="text-white px-2 py-1 rounded overflow-auto hover:bg-custom-600"
                        >
                          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5"></ChevronLeft>
                        </button>
                        <button
                          onClick={() => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                          className="text-white px-2 sm:px-4 py-1 mx-1 sm:mx-2 rounded overflow-auto hover:bg-custom-600 text-xs sm:text-sm"
                        >
                          Today
                        </button>
                        <button
                          onClick={handleNextWeek}
                          className="text-white px-2 py-1 rounded overflow-auto hover:bg-custom-600"
                        >
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5"></ChevronRight>
                        </button>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] sm:max-h-[calc(100vh-300px)]">
                        <table className="w-full border-collapse">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-50 min-w-[120px] bg-gray-50 border border-gray-200">Staff Name</th>
                              {daysOfWeek.map((day, index) => {
                                const isToday = format(new Date(), 'EEE, MMM d') === day;
                                return (
                                  <th
                                    key={index}
                                    className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider sticky top-0 z-50 min-w-[140px] border border-gray-200 ${isToday ? 'bg-blue-50 text-blue-700 font-bold' : 'bg-gray-50 text-gray-500'
                                      }`}
                                  >
                                    <span>{day}</span>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {paginatedData.length > 0 ? (
                              paginatedData.map((item: any) => (
                                <tr key={item.userId} className="hover:bg-gray-50 transition-colors">
                                  <td
                                    className="px-4 py-4 border border-gray-200 cursor-pointer bg-gray-50/50"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      getDetailCalendar(item);
                                    }}
                                  >
                                    <div className="flex items-center">
                                      <span className="text-sm font-medium text-gray-900">{item.name.charAt(0).toUpperCase() + item.name.slice(1)}</span>
                                    </div>
                                  </td>
                                  {daysOfWeek.map((day, index) => (
                                    <td key={index} className="px-4 py-4 text-center border border-gray-200 min-h-[80px] align-top">
                                      {item?.events?.length > 0 && item.events
                                        .filter((event: any) => format(parseISO(event.startTime), 'EEE, MMM d') === day)
                                        .map((event: any) => (
                                          <div key={event.id}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleEventClick(event);
                                            }}
                                            className="bg-blue-100 border border-blue-200 text-blue-800 py-2 px-3 mb-2 rounded-md cursor-pointer hover:bg-blue-200 transition-colors text-xs font-medium">
                                            <span className="hidden sm:inline">{`${format(new Date(event.startTime), 'h:mm a')} - ${format(new Date(event.endTime), 'h:mm a')}`}</span>
                                            <span className="sm:hidden">{`${format(new Date(event.startTime), 'h:mm')} - ${format(new Date(event.endTime), 'h:mm')}`}</span>
                                          </div>
                                        ))}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={daysOfWeek.length + 1} className="px-4 py-12 text-center border border-gray-200">
                                  <div className="flex flex-col items-center">
                                    <div className="w-6 h-6 mx-auto mb-4 text-gray-400">
                                      <Search className="w-full h-full" />
                                    </div>
                                    <h3 className="text-sm font-medium text-gray-900 mb-1">No staff schedules found</h3>
                                    <p className="text-gray-500">Try adjusting your search or filters.</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* Pagination */}
                    <div className="bg-white py-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                        {/* Left side - Page navigation */}
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>

                          <span className="text-sm text-gray-600 px-2 min-w-[60px] text-center">
                            {currentPage} / {totalPages}
                          </span>

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Center - Results info */}
                        <div className="flex items-center m-auto sm:m-0">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">{totalItems}</span>
                          </p>
                        </div>

                        {/* Right side - Page size options */}
                        <div className="items-center justify-center sm:justify-end space-x-4 hidden sm:flex">
                          {pageSizeOptions.map((size) => (
                            <button
                              key={size}
                              onClick={() => {
                                setPageSize(size);
                                setCurrentPage(1);
                              }}
                              className={`text-sm transition-colors px-2 py-1 rounded ${pageSize === size
                                ? 'text-blue-600 font-medium bg-blue-50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 mx-auto text-gray-400">
                          <Search className="w-6 h-6" />
                        </div>
                        <h3 className="text-sm font-medium text-gray-900 mb-1">No staff schedules found</h3>
                        {/* <p className="text-gray-500">Try adjusting your search or check availability filters.</p> */}
                      </div>
                    </div>
                  </div>
                )}</>)}
          </div>
        </div >
      </div>
    </React.Fragment>
  )
}

export default StaffSchedule